import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { type SolarDay, solarApi } from "@/api/solar";
import { PeriodNav } from "@/components/PeriodNav";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PLANT_COLORS } from "@/lib/segmentColors";

import {
  type Period,
  calendarRangeFor,
  fmt as fmtCanonical,
  num as numCanonical,
  todayIso,
} from "@/lib/format";

const num = (v: number | string | null | undefined): number => numCanonical(v) ?? 0;
const fmt = (n: number | null, digits = 0): string => fmtCanonical(n, digits);

/** Сторінка «СЕС» (FR-SOLAR-08). Показує відпуск у мережу, виручку за
 *  фіксованим тарифом, споживання станції з мережі та прибуток. */
export function SolarPage() {
  const { t } = useTranslation();
  const [end, setEnd] = useState(todayIso());
  const [period, setPeriod] = useState<Period>("month");
  const [plantId, setPlantId] = useState<string | null>(null); // null = всі станції

  const { from, to } = useMemo(() => calendarRangeFor(end, period), [end, period]);

  const plants = useQuery({
    queryKey: ["solar-plants"],
    queryFn: () => solarApi.listPlants(),
  });

  const daily = useQuery({
    queryKey: ["solar-daily", from, to, plantId],
    queryFn: () => solarApi.daily(from, to, { plantId: plantId ?? undefined }),
  });

  const plantName = (id: string | null): string => {
    if (!id) return t("solar.all_plants");
    return plants.data?.find((p) => p.id === id)?.name ?? id.slice(0, 8);
  };

  const totals = useMemo(() => {
    const rows = daily.data ?? [];
    let exportKwh = 0;
    let importKwh = 0;
    let revenue = 0;
    let profit = 0;
    for (const r of rows) {
      exportKwh += num(r.export_kwh);
      importKwh += num(r.import_kwh);
      revenue += num(r.revenue_uah);
      profit += num(r.profit_uah);
    }
    return { exportKwh, importKwh, revenue, profit };
  }, [daily.data]);

  /** Дані графіка: по днях, серія на кожну станцію (або одна, якщо обрана). */
  const chart = useMemo(() => {
    const rows = daily.data ?? [];
    const byDay = new Map<string, Record<string, number | string>>();
    for (const r of rows) {
      const key = r.day.slice(5);
      const row = byDay.get(key) ?? { day: key };
      const series = r.plant_id ? plantName(r.plant_id) : t("solar.all_plants");
      row[series] = num(row[series] as number) + num(r.export_kwh);
      byDay.set(key, row);
    }
    return Array.from(byDay.values());
  }, [daily.data, plants.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const seriesNames = useMemo(() => {
    const s = new Set<string>();
    for (const r of daily.data ?? []) {
      s.add(r.plant_id ? plantName(r.plant_id) : t("solar.all_plants"));
    }
    return Array.from(s);
  }, [daily.data, plants.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ті самі кольори станцій, що й на «Фінансах» (зелений там зайнятий КГУ).
  const COLORS = PLANT_COLORS;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("solar.title")}</CardTitle>
          <CardDescription>{t("solar.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <PeriodNav
            period={period}
            refDate={end}
            onPeriodChange={setPeriod}
            onRefDateChange={setEnd}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={plantId === null ? "default" : "outline"}
              size="sm"
              onClick={() => setPlantId(null)}
            >
              {t("solar.all_plants")}
            </Button>
            {(plants.data ?? []).map((p) => (
              <Button
                key={p.id}
                variant={plantId === p.id ? "default" : "outline"}
                size="sm"
                onClick={() => setPlantId(p.id)}
              >
                {p.name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {(plants.data ?? []).length === 0 && !plants.isLoading && (
        <Card>
          <CardContent className="p-4 text-sm text-slate-500">
            {t("solar.no_plants")}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat
          label={t("solar.export_total")}
          value={`${fmt(totals.exportKwh / 1000, 1)} ${t("statistics.unit_mwh")}`}
        />
        <Stat label={t("solar.revenue_total")} value={`${fmt(totals.revenue, 0)} ${t("common.uah")}`} />
        <Stat
          label={t("solar.import_total")}
          value={`${fmt(totals.importKwh, 1)} ${t("common.kwh")}`}
        />
        <Stat label={t("solar.profit_total")} value={`${fmt(totals.profit, 0)} ${t("common.uah")}`} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("solar.chart_export")}</CardTitle>
        </CardHeader>
        <CardContent>
          {chart.length === 0 ? (
            <div className="text-sm text-slate-500">{t("home1.no_data")}</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis tickFormatter={(v) => fmt(v as number)} />
                  <Tooltip formatter={(v) => fmt(v as number)} />
                  <Legend />
                  {seriesNames.map((name, i) => (
                    <Bar
                      key={name}
                      dataKey={name}
                      stackId="export"
                      fill={COLORS[i % COLORS.length]}
                      name={name}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("solar.table_title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <DailyTable rows={daily.data ?? []} plantName={plantName} />
        </CardContent>
      </Card>
    </div>
  );
}

function DailyTable({
  rows,
  plantName,
}: {
  rows: SolarDay[];
  plantName: (id: string | null) => string;
}) {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return <div className="text-sm text-slate-500">{t("home1.no_data")}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500">
            <th className="p-2">{t("solar.col_day")}</th>
            <th className="p-2">{t("solar.col_plant")}</th>
            <th className="p-2 text-right">{t("solar.col_export")}</th>
            <th className="p-2 text-right">{t("solar.col_import")}</th>
            <th className="p-2 text-right">{t("solar.col_revenue")}</th>
            <th className="p-2 text-right">{t("solar.col_profit")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.day}-${r.plant_id ?? "all"}-${i}`} className="border-t border-slate-100">
              <td className="p-2">{r.day}</td>
              <td className="p-2">{plantName(r.plant_id)}</td>
              <td className="p-2 text-right font-mono">{fmt(num(r.export_kwh), 1)}</td>
              <td className="p-2 text-right font-mono">{fmt(num(r.import_kwh), 1)}</td>
              <td className="p-2 text-right font-mono">{fmt(num(r.revenue_uah), 0)}</td>
              <td className="p-2 text-right font-mono">{fmt(num(r.profit_uah), 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-xl font-mono font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}
